import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { LuPlus, LuPencil, LuTrash2, LuGripVertical, LuInfo, LuLogIn } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import { FunilService, PAPEL, type EtapaFunil } from '../services/FunilService';
import './FunilPage.css';

/**
 * O funil que a empresa desenha em COLUNAS RETANGULARES (formato Kanban / Jira).
 * As etapas seguem em fluxo contínuo centralizado na tela:
 * [ Novo (Etapa padrão) ] ➔ [ Miolo da Empresa... ] ➔ [ + Adicionar ] ➔ [ Ganho ] ➔ [ Perdido ]
 */

/* A cor é do lugar na fila, não do banco: a mesma paleta que o quadro usa para as colunas. */
const CORES = ['var(--fn-ia)', 'var(--fn-qual)', 'var(--fn-prop)', 'var(--fn-neg)', 'var(--fn-extra)'];

const corDaEtapa = (etapa: EtapaFunil, indiceNoMiolo: number) => {
    if (etapa.papel === PAPEL.ganho) return 'var(--fn-win)';
    if (etapa.papel === PAPEL.perdido) return 'var(--fn-lost)';
    if (!etapa.editavel) return 'var(--fn-ia)';
    return CORES[(indiceNoMiolo % (CORES.length - 1)) + 1];
};

const regraDaEtapa = (papel: number | null) => {
    if (papel === PAPEL.roboAtendendo) return 'O robô atende enquanto o negócio está aqui';
    if (papel === PAPEL.roboEncerrado) return 'O robô encerrou e ninguém assumiu';
    if (papel === PAPEL.ganho) return 'Pede o produto vendido · conta como venda nas metas';
    if (papel === PAPEL.perdido) return 'Pede o motivo da perda';
    return '';
};

const FunilPage = () => {
    const [etapas, setEtapas] = useState<EtapaFunil[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);

    const [editando, setEditando] = useState<number | null>(null);
    const [nomeEditado, setNomeEditado] = useState('');
    const [excluindo, setExcluindo] = useState<number | null>(null);
    const [destino, setDestino] = useState<number | null>(null);
    const [criando, setCriando] = useState(false);
    const [nomeNovo, setNomeNovo] = useState('');

    const campoEdicao = useRef<HTMLInputElement>(null);
    const campoNovo = useRef<HTMLInputElement>(null);

    const carregar = useCallback(async () => {
        try {
            setEtapas(await FunilService.listar());
        } catch (e: unknown) {
            aviso.erro(e instanceof Error ? e.message : SEM_SERVIDOR);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => { if (editando !== null) campoEdicao.current?.focus(); }, [editando]);
    useEffect(() => { if (criando) campoNovo.current?.focus(); }, [criando]);

    const miolo = useMemo(() => etapas.filter(e => e.editavel), [etapas]);
    const ultimaDoMiolo = miolo[miolo.length - 1];

    const fechar = () => {
        setEditando(null);
        setExcluindo(null);
        setCriando(false);
        setNomeNovo('');
    };

    const comSalvamento = async (acao: () => Promise<void>) => {
        setSalvando(true);
        try {
            await acao();
            await carregar();
            fechar();
        } catch (e: unknown) {
            aviso.erro(e instanceof Error ? e.message : SEM_SERVIDOR);
        } finally {
            setSalvando(false);
        }
    };

    const renomear = (etapa: EtapaFunil) => {
        const nome = nomeEditado.trim();
        if (!nome || nome === etapa.nome) { fechar(); return; }
        comSalvamento(async () => {
            await FunilService.renomear(etapa.id, nome);
            aviso.ok('Etapa renomeada.');
        });
    };

    const criar = () => {
        const nome = nomeNovo.trim();
        if (!nome) { fechar(); return; }
        comSalvamento(async () => {
            await FunilService.criar(nome, ultimaDoMiolo?.id);
            aviso.ok('Etapa criada.');
        });
    };

    const excluir = (etapa: EtapaFunil) => {
        if (destino === null) return;
        comSalvamento(async () => {
            const mensagem = await FunilService.excluir(etapa.id, destino);
            aviso.ok(mensagem);
        });
    };

    const marcarEntrada = (etapa: EtapaFunil) => comSalvamento(async () => {
        await FunilService.definirEntradaDoLead(etapa.id);
        aviso.ok('Entrada do lead atualizada.');
    });

    const aoSoltar = (resultado: DropResult) => {
        if (!resultado.destination || resultado.destination.index === resultado.source.index) return;

        const ordem = [...miolo];
        const [movida] = ordem.splice(resultado.source.index, 1);
        ordem.splice(resultado.destination.index, 0, movida);

        const ids = ordem.map(e => e.id);
        setEtapas(atual => [...atual].sort((a, b) => {
            const ia = ids.indexOf(a.id), ib = ids.indexOf(b.id);
            if (ia === -1 || ib === -1) return a.ordem - b.ordem;
            return ia - ib;
        }));

        FunilService.reordenar(ids)
            .then(carregar)
            .catch((e: unknown) => {
                aviso.erro(e instanceof Error ? e.message : SEM_SERVIDOR);
                carregar();
            });
    };

    const colunaDeSistema = (etapa: EtapaFunil, posicao: 'inicio' | 'fim') => {
        const cor = corDaEtapa(etapa, -1);
        const regra = regraDaEtapa(etapa.papel);
        const labelSistema = posicao === 'inicio' ? 'Entrada padrão' : 'Desfecho padrão';

        return (
            <div className="fn-col is-sistema" key={etapa.id}>
                <div className="fn-col-header">
                    <div className="fn-col-header-left">
                        <span className="fn-dot" style={{ background: cor }} aria-hidden="true" />
                        <span className="fn-col-nome">{etapa.nome}</span>
                    </div>
                    <span className="fn-col-tag-sistema">Etapa padrão</span>
                </div>

                <div className="fn-col-body">
                    <div className="fn-col-neg-box">
                        <span className="fn-col-neg-num">{etapa.negocios}</span>
                        <span className="fn-col-neg-label">negócio{etapa.negocios === 1 ? '' : 's'}</span>
                    </div>

                    {regra && (
                        <div className="fn-col-regra-box">
                            <LuInfo size={13} className="fn-col-regra-icon" />
                            <span>{regra}</span>
                        </div>
                    )}
                </div>

                <div className="fn-col-footer is-sistema">
                    <span className="fn-col-tipo-desc">{labelSistema}</span>
                </div>
            </div>
        );
    };

    const colunaDoMiolo = (etapa: EtapaFunil, indice: number) => {
        if (excluindo === etapa.id) {
            const opcoes = miolo.filter(e => e.id !== etapa.id);
            return (
                <div className="fn-col is-excluindo" role="alert" key={etapa.id}>
                    <div className="fn-conf-header">
                        <LuTrash2 size={16} className="fn-conf-icon" />
                        <span className="fn-conf-title">Excluir "{etapa.nome}"?</span>
                    </div>

                    <div className="fn-conf-body">
                        <p className="fn-conf-text">
                            {etapa.negocios > 0
                                ? `Mover os ${etapa.negocios} negócio${etapa.negocios === 1 ? '' : 's'} para:`
                                : 'Sem negócios nesta etapa; ela sairá do quadro.'}
                        </p>

                        {etapa.negocios >= 0 && (
                            <select
                                className="fn-sel"
                                value={destino ?? ''}
                                onChange={e => setDestino(Number(e.target.value))}
                                aria-label="Para qual etapa os negócios vão"
                            >
                                {opcoes.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                            </select>
                        )}
                    </div>

                    <div className="fn-conf-actions">
                        <button type="button" className="fn-btn-cancel" onClick={fechar} disabled={salvando}>
                            Não
                        </button>
                        <button type="button" className="fn-btn-danger" onClick={() => excluir(etapa)} disabled={salvando}>
                            {salvando ? 'Aguarde…' : 'Excluir'}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <Draggable draggableId={String(etapa.id)} index={indice} key={etapa.id}>
                {(provided, snapshot) => (
                    <div
                        className={`fn-col${snapshot.isDragging ? ' is-arrastando' : ''}`}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                    >
                        <div className="fn-col-header">
                            <div className="fn-col-header-left">
                                <span
                                    className="fn-alca"
                                    {...provided.dragHandleProps}
                                    aria-label="Arrastar para reordenar"
                                    title="Arrastar para reordenar etapa"
                                >
                                    <LuGripVertical size={15} />
                                </span>
                                <span
                                    className="fn-dot"
                                    style={{ background: corDaEtapa(etapa, indice) }}
                                    aria-hidden="true"
                                />
                                {editando === etapa.id ? (
                                    <input
                                        ref={campoEdicao}
                                        className="fn-col-input-edit"
                                        value={nomeEditado}
                                        maxLength={100}
                                        onChange={e => setNomeEditado(e.target.value)}
                                        onBlur={() => renomear(etapa)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') renomear(etapa);
                                            if (e.key === 'Escape') fechar();
                                        }}
                                    />
                                ) : (
                                    <span className="fn-col-nome" title={etapa.nome}>
                                        {etapa.nome}
                                    </span>
                                )}
                            </div>

                            <div className="fn-col-header-actions">
                                {editando !== etapa.id && (
                                    <>
                                        <button
                                            type="button"
                                            className="fn-action-btn"
                                            onClick={() => {
                                                setEditando(etapa.id);
                                                setNomeEditado(etapa.nome);
                                            }}
                                            aria-label={`Renomear ${etapa.nome}`}
                                            title="Renomear etapa"
                                        >
                                            <LuPencil size={14} />
                                        </button>
                                        {miolo.length > 1 && (
                                            <button
                                                type="button"
                                                className="fn-action-btn is-delete"
                                                onClick={() => {
                                                    setExcluindo(etapa.id);
                                                    setDestino(miolo.find(e => e.id !== etapa.id)?.id ?? null);
                                                }}
                                                aria-label={`Excluir ${etapa.nome}`}
                                                title="Excluir etapa"
                                            >
                                                <LuTrash2 size={14} />
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {editando === etapa.id && (
                            <span className="fn-ajuda-inline">Enter salva · Esc volta</span>
                        )}

                        <div className="fn-col-body">
                            <div className="fn-col-neg-box">
                                <span className="fn-col-neg-num">{etapa.negocios}</span>
                                <span className="fn-col-neg-label">negócio{etapa.negocios === 1 ? '' : 's'}</span>
                            </div>

                            <div className="fn-col-lead-status">
                                {etapa.entradaDoLead ? (
                                    <span className="fn-chip" title="Novos leads entram por esta etapa">
                                        <LuLogIn size={13} />
                                        <span>Entrada do lead</span>
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        className="fn-btn-definir-entrada"
                                        onClick={() => marcarEntrada(etapa)}
                                        title="Definir esta etapa como a porta de entrada dos leads"
                                    >
                                        <LuLogIn size={13} />
                                        <span>Definir como entrada</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="fn-col-footer">
                            <span className="fn-col-ordem-badge">Etapa {indice + 1} do miolo</span>
                        </div>
                    </div>
                )}
            </Draggable>
        );
    };

    /**
     * "Novo" é uma coluna só. Atendimento IA e IA encerrada são estados do robô, não etapas de
     * venda — o quadro já as mostra juntas, e aqui some até o nome: o que a empresa vê é onde o
     * robô trabalha, com a conta dos dois dentro.
     */
    const doRobo = etapas.filter(e => e.papel === PAPEL.roboAtendendo || e.papel === PAPEL.roboEncerrado);
    const antesDoMiolo: EtapaFunil[] = doRobo.length === 0 ? [] : [{
        ...doRobo[0],
        negocios: doRobo.reduce((soma, e) => soma + e.negocios, 0),
    }];
    const depoisDoMiolo = etapas.filter(e => e.papel === PAPEL.ganho || e.papel === PAPEL.perdido);

    return (
        <div className="fn-screen">
            <PageHeader
                title="Funil"
                subtitle="As etapas pelas quais um negócio passa — são as colunas do quadro de oportunidades. Arraste para reordenar."
            />

            {carregando ? (
                <p className="fn-vazio">Carregando o funil…</p>
            ) : (
                <div className="fn-board-wrapper">
                    <div className="fn-board">
                        {antesDoMiolo.map(e => colunaDeSistema(e, 'inicio'))}

                        <DragDropContext onDragEnd={aoSoltar}>
                            <Droppable droppableId="miolo" direction="horizontal">
                                {provided => (
                                    <div
                                        className="fn-miolo-cols"
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {miolo.map((etapa, i) => colunaDoMiolo(etapa, i))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        {criando ? (
                            <div className="fn-col is-criando">
                                <div className="fn-col-header">
                                    <span className="fn-dot" style={{ background: 'var(--fn-extra)' }} aria-hidden="true" />
                                    <span className="fn-col-nome">Nova etapa</span>
                                </div>

                                <div className="fn-col-body">
                                    <input
                                        ref={campoNovo}
                                        className="fn-col-input-novo"
                                        value={nomeNovo}
                                        maxLength={100}
                                        placeholder="Nome da etapa"
                                        onChange={e => setNomeNovo(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') criar();
                                            if (e.key === 'Escape') fechar();
                                        }}
                                    />
                                    <span className="fn-ajuda-inline">Enter cria · Esc volta</span>
                                </div>

                                <div className="fn-conf-actions">
                                    <button type="button" className="fn-btn-cancel" onClick={fechar} disabled={salvando}>
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="fn-btn-primary"
                                        onClick={criar}
                                        disabled={salvando || !nomeNovo.trim()}
                                    >
                                        {salvando ? 'Criando…' : 'Criar etapa'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="fn-col-add-btn"
                                onClick={() => setCriando(true)}
                                title="Adicionar uma nova etapa ao miolo do funil"
                            >
                                <div className="fn-col-add-icon">
                                    <LuPlus size={20} />
                                </div>
                                <span className="fn-col-add-text">Adicionar etapa</span>
                            </button>
                        )}

                        {depoisDoMiolo.map(e => colunaDeSistema(e, 'fim'))}
                    </div>

                    <p className="fn-pe">
                        <LuInfo size={14} />
                        <span>
                            As etapas padrão carregam regras do produto e por isso não se
                            editam, não se apagam e não mudam de lugar.
                        </span>
                    </p>
                </div>
            )}
        </div>
    );
};

export default FunilPage;
